import React, { useState, useEffect } from "react";
import { useToast } from "../../../contexts/ToastContext";
import {
  getEventAttendees,
  ngoMarkAsPaid,
  ngoMarkAsUnPaid,
  viewAttendeePaymentReceipt,
} from "../../../services/eventService";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";

export interface User {
  id: number | null;
  name: string;
  email: string;
}

export interface EventAttendee {
  User: User;
  registered_at: string;
  cancelled_at: string | null;
  status: "registered" | "cancelled";
  status_label: "Registered" | "Cancelled";
  registered_at_formatted: string;
  cancelled_at_formatted: string | null;
  amount: number;
  donation_amount: number;
  payment_status: string;
  payment_status_label: string;
  payment_id: string;
  refund_id: string | null;
}

const useEventAttendeeListLogic = () => {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.is_admin || false;
  const { id } = useParams(); // <-- this gets "2" from /events/edit/2
  const eventId = id ? parseInt(id, 10) : null;

  const { showSuccessToast, showErrorToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendees, setEventAttendees] = useState<EventAttendee[]>([]);
  const [eventDetails, setEventDetails] = useState<any>(null); // Replace 'any' with the actual type if available

  const fetchEventAttendees = async (eventId: number) => {
    if (!eventId) {
      return;
    }
    try {
      setLoading(true);
      const res = await getEventAttendees(eventId);
      setEventAttendees(res.data.data.attendees);
      setEventDetails(res.data.data.eventDetails);
      setTotalDonationAmount(res.data.data.totalDonation);
      setTotalAmount(res.data.data.totalAmount);
    } catch (err) {
      console.error("Error fetching causes:", err);
    } finally {
      setLoading(false);
    }
  };

  const [paymentReceiptLoading, setPaymentReceiptLoading] = useState(false);

  const handleShowPaymentReceipt = async (userId: number) => {
    try {
      if (paymentReceiptLoading) return;
      setPaymentReceiptLoading(true);
      if (!eventId) return;
      if (!userId) return;
      const response = await viewAttendeePaymentReceipt(eventId, userId);
      window.open(
        response.data.data.paymentIntent.latest_charge.receipt_url,
        "_blank"
      );
      showSuccessToast("Payment receipt opened in a new tab.");
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(errorResponse?.message || "Failed to fetch receipt.");
    } finally {
      setPaymentReceiptLoading(false);
    }
  };

  const [totalDonationAmount, setTotalDonationAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const [markAsPaidLoading, setMarkAsPaidLoading] = useState(false);

  const handleMarkAsPaid = async (ngoId: number) => {
    try {
      if (!eventId) return;
      if (!ngoId) return;
      setMarkAsPaidLoading(true);
      const response = await ngoMarkAsPaid(eventId, ngoId);
      showSuccessToast(response.data.message);
      fetchEventAttendees(eventId);
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(errorResponse?.message || "Failed to mark as paid.");
    } finally {
      setMarkAsPaidLoading(false);
    }
  };

  const handleMarkAsUnPaid = async (ngoId: number) => {
    try {
      if (!eventId) return;
      if (!ngoId) return;
      setMarkAsPaidLoading(true);
      const response = await ngoMarkAsUnPaid(eventId, ngoId);
      showSuccessToast(response.data.message);
      fetchEventAttendees(eventId);
    } catch (error) {
      const errorResponse = (error as any)?.response?.data || error;
      showErrorToast(errorResponse?.message || "Failed to mark as paid.");
    } finally {
      setMarkAsPaidLoading(false);
    }
  };

  useEffect(() => {
    if (!eventId) {
      showErrorToast("Invalid Event.");
      navigate("/events");
    } else {
      fetchEventAttendees(eventId);
    }
  }, []);

  return {
    isAdmin,
    attendees,
    eventDetails,
    loading,
    navigate,
    paymentReceiptLoading,
    handleShowPaymentReceipt,
    totalDonationAmount,
    totalAmount,
    handleMarkAsPaid,
    markAsPaidLoading,
    handleMarkAsUnPaid
  };
};

export default useEventAttendeeListLogic;
